import { useState } from "react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { RosterEntry } from "./types";

/**
 * MoveAcuvimDialog — confirms a system_id's transition into (or first-time
 * binding to) the active window. Carries forward the prior membership's
 * service_name and service_capacity_amps unless the operator opts to change them.
 *
 * Extracted from routes/AdminLoadIn.tsx (2026-05-27).
 */
export function MoveAcuvimDialog({
  target,
  windowId,
  windowName,
  onClose,
  onDone,
}: {
  target: RosterEntry;
  windowId: string;
  windowName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  // Carryover candidates come from the OLD event membership (if any). Display
  // them, let the user keep-or-replace each.
  const oldName = target.other_event?.service_name ?? null;
  const oldCap = target.other_event?.service_capacity_amps ?? null;
  const isMove = target.other_event !== null;

  const [carryName, setCarryName] = useState(oldName !== null);
  const [carryCap, setCarryCap] = useState(oldCap !== null);
  const [nameInput, setNameInput] = useState("");
  const [capInput, setCapInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confirm = async () => {
    setErr(null);

    // Validate capacity when user is supplying a new one (carry off + non-empty).
    let bodyCap: number | null = null;
    if (!carryCap) {
      const raw = capInput.trim();
      if (raw !== "") {
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 1 || n > 6000) {
          setErr("Capacity must be an integer 1–6000, or blank.");
          return;
        }
        bodyCap = n;
      }
    }

    setSaving(true);
    try {
      await api.post("/api/memberships/move", {
        system_id: target.system_id,
        to_window_id: windowId,
        carry_service_name: carryName,
        carry_capacity: carryCap,
        service_name: carryName ? null : (nameInput.trim() || null),
        service_capacity_amps: carryCap ? null : bodyCap,
      });
      onDone();
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string };
        setErr(body?.error ?? `Server error ${e.status}`);
      } else {
        setErr(String(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isMove ? "Move" : "Add"} {target.system_id} to {windowName}
          </DialogTitle>
          {isMove && target.other_event && !target.other_event.redacted && (
            <DialogDescription>
              Currently bound to{" "}
              <span className="font-medium">
                {target.other_event.window_name ?? "another event"}
              </span>
              . That membership will be closed.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Service name</Label>
            {oldName !== null ? (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  Existing: <span className="font-mono">{oldName}</span>
                </div>
                <div className="flex gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={carryName}
                      onChange={() => setCarryName(true)}
                    />
                    Keep
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={!carryName}
                      onChange={() => setCarryName(false)}
                    />
                    Change
                  </label>
                </div>
                {!carryName && (
                  <Input
                    value={nameInput}
                    placeholder="e.g. FOH L Main (blank to clear)"
                    onChange={(e) => setNameInput(e.target.value)}
                  />
                )}
              </div>
            ) : (
              <Input
                value={nameInput}
                placeholder="e.g. FOH L Main (optional)"
                onChange={(e) => setNameInput(e.target.value)}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Capacity (A)</Label>
            {oldCap !== null ? (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  Existing: <span className="font-mono">{oldCap}A</span>
                </div>
                <div className="flex gap-3 text-sm">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={carryCap}
                      onChange={() => setCarryCap(true)}
                    />
                    Keep
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={!carryCap}
                      onChange={() => setCarryCap(false)}
                    />
                    Change
                  </label>
                </div>
                {!carryCap && (
                  <Input
                    type="number"
                    min={1}
                    max={6000}
                    step={1}
                    value={capInput}
                    placeholder="400 (blank to clear)"
                    onChange={(e) => setCapInput(e.target.value)}
                  />
                )}
              </div>
            ) : (
              <Input
                type="number"
                min={1}
                max={6000}
                step={1}
                value={capInput}
                placeholder="400 (optional)"
                onChange={(e) => setCapInput(e.target.value)}
              />
            )}
          </div>

          {err && <div className="text-destructive text-sm">{err}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={saving}>
            {saving ? "…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
